/** Stored on user.admissionPermissions when "Admission" menu is enabled */

export const ADMISSION_SECTION_KEYS = {
  CREATE: "createAdmission",
  EDIT: "editAdmission",
  VIEW: "viewAdmission",
  REMINDER: "paymentReminder",
  BANK: "bankAccount",
} as const;

export const ADMISSION_SECTION_OPTIONS: { key: string; label: string }[] = [
  { key: ADMISSION_SECTION_KEYS.CREATE, label: "Create admission" },
  { key: ADMISSION_SECTION_KEYS.EDIT, label: "Edit admission" },
  { key: ADMISSION_SECTION_KEYS.VIEW, label: "View admission (list & details)" },
  { key: ADMISSION_SECTION_KEYS.REMINDER, label: "Payment reminder" },
  { key: ADMISSION_SECTION_KEYS.BANK, label: "Bank accounts" },
];

export const DEFAULT_ADMISSION_SECTION_KEYS = ADMISSION_SECTION_OPTIONS.map(
  (o) => o.key,
);

export function isCompanyAdmin(signinuser: any): boolean {
  return signinuser?.UserType === 1 || signinuser?.UserType === 2;
}

export function isApproverDesignation(signinuser: any): boolean {
  const d = String(signinuser?.designation || "").trim().toLowerCase();
  const p = String(signinuser?.UserProfile || "").trim().toLowerCase();
  return d === "approver" || p === "approver";
}

/** Legacy team users: no array or empty = full admission sections */
export function hasLegacyFullAdmissionSections(signinuser: any): boolean {
  if (isCompanyAdmin(signinuser)) return true;
  if (signinuser?.UserType !== 3) return false;
  const p = signinuser?.admissionPermissions;
  return !Array.isArray(p) || p.length === 0;
}

export function hasAdmissionSection(signinuser: any, key: string): boolean {
  if (isCompanyAdmin(signinuser)) return true;
  if (signinuser?.UserType !== 3) return false;
  if (hasLegacyFullAdmissionSections(signinuser)) return true;
  return (
    Array.isArray(signinuser.admissionPermissions) &&
    signinuser.admissionPermissions.includes(key)
  );
}

export function hasAdmissionMenu(signinuser: any): boolean {
  if (isCompanyAdmin(signinuser)) return true;
  return (
    Array.isArray(signinuser?.menuPermissions) &&
    signinuser.menuPermissions.includes("Admission")
  );
}

export function canDeleteAdmission(signinuser: any): boolean {
  return signinuser?.UserType === 1 || signinuser?.UserType === 2;
}

export function canAccessAdmissionPath(
  signinuser: any,
  pathname: string,
  locationState?: any,
): boolean {
  const base = (pathname.split("?")[0] || pathname).replace(/\/$/, "") || "/";

  const admissionPaths =
    base.startsWith("/admission") ||
    base === "/account-list" ||
    base === "/account-form";

  if (!admissionPaths) return true;

  if (!hasAdmissionMenu(signinuser)) return false;

  if (base === "/admission-dashboard") return true;

  if (base === "/admission-list") {
    return (
      hasAdmissionSection(signinuser, ADMISSION_SECTION_KEYS.VIEW) ||
      isApproverDesignation(signinuser)
    );
  }

  if (base === "/view-admission") {
    return (
      hasAdmissionSection(signinuser, ADMISSION_SECTION_KEYS.VIEW) ||
      isApproverDesignation(signinuser)
    );
  }

  if (base === "/admission-form") {
    const isEdit = !!(locationState && (locationState as any)._id);
    if (isEdit) {
      return hasAdmissionSection(signinuser, ADMISSION_SECTION_KEYS.EDIT);
    }
    return hasAdmissionSection(signinuser, ADMISSION_SECTION_KEYS.CREATE);
  }

  if (base === "/account-list" || base === "/account-form") {
    return hasAdmissionSection(signinuser, ADMISSION_SECTION_KEYS.BANK);
  }

  return true;
}

export function defaultAdmissionRedirect(signinuser: any): string {
  if (hasAdmissionMenu(signinuser)) return "/admission-dashboard";
  return "/unauthorized";
}

export function canShowNavbarAdmissionLink(
  signinuser: any,
  link: string,
): boolean {
  const path = link.split("?")[0];
  return canAccessAdmissionPath(signinuser, path, undefined);
}
